import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  ExternalLink,
  Loader2,
  Send,
  XCircle,
} from "lucide-react";
import { SOCIAL_PLATFORMS, type DraftPublicationDTO } from "@app/shared";
import { api } from "../lib/api";
import { useActiveBrand } from "../lib/brand";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { useToast } from "./Toast";

function msg(e: unknown): string {
  return e instanceof Error ? e.message : "Terjadi kesalahan";
}

const PUBLISHABLE = SOCIAL_PLATFORMS.filter((p) => p.supportsDraftPublish);

/**
 * Cross-post a draft (its image + caption) to the active brand's connected,
 * publish-capable social platforms. Per-platform status is shown after sending.
 * Decoupled from the Instagram approve→schedule flow.
 */
export function DraftPublish({ draftId }: { draftId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const brandKey = useActiveBrand();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const connQuery = useQuery({
    queryKey: ["social-connections", brandKey],
    queryFn: ({ signal }) => api.socialConnections(brandKey as string, signal),
    enabled: !!brandKey,
  });
  const pubQuery = useQuery({
    queryKey: ["draft-publications", draftId],
    queryFn: ({ signal }) => api.draftPublications(draftId, signal),
  });

  const connected = new Set(
    (connQuery.data ?? []).filter((c) => c.connected).map((c) => c.platform),
  );
  const candidates = PUBLISHABLE.filter((p) => connected.has(p.key));
  const pubByPlatform = new Map(
    (pubQuery.data ?? []).map((p) => [p.platform, p]),
  );

  const publish = useMutation({
    mutationFn: () => api.publishSocial(draftId, [...selected]),
    onSuccess: (rows) => {
      const ok = rows.filter((r) => r.status === "published").length;
      const failed = rows.filter((r) => r.status === "failed").length;
      if (failed === 0) toast.success(`Dipublikasikan ke ${ok} platform`);
      else toast.error(`${ok} berhasil, ${failed} gagal`);
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: ["draft-publications", draftId] });
    },
    onError: (e) => toast.error(msg(e)),
  });

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (!brandKey) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <Send className="h-4 w-4 text-navy" />
          Publikasikan ke sosial media
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {candidates.length === 0 ? (
          <p className="rounded-md bg-slate-100 p-2 text-xs text-slate-500">
            Belum ada platform yang siap. Hubungkan Bluesky atau X di Konfigurasi
            Brand untuk bisa publikasi dari sini.
          </p>
        ) : (
          <>
            <div className="space-y-2">
              {candidates.map((p) => {
                const pub = pubByPlatform.get(p.key);
                return (
                  <label
                    key={p.key}
                    className="flex items-center gap-3 rounded-md border border-slate-200 p-2.5"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(p.key)}
                      onChange={() => toggle(p.key)}
                      className="h-4 w-4"
                    />
                    <span
                      className="h-6 w-6 shrink-0 rounded"
                      style={{ backgroundColor: p.color }}
                      aria-hidden
                    />
                    <span className="flex-1 text-sm font-medium text-navy">
                      {p.label}
                    </span>
                    <PublicationStatusLabel pub={pub} />
                  </label>
                );
              })}
            </div>
            <Button
              variant="primary"
              onClick={() => publish.mutate()}
              disabled={publish.isPending || selected.size === 0}
            >
              {publish.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Publikasikan ({selected.size})
            </Button>
            <p className="text-[11px] text-slate-400">
              Memakai gambar &amp; caption draft ini. Instagram tetap lewat tombol
              Approve &amp; schedule.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PublicationStatusLabel({ pub }: { pub: DraftPublicationDTO | undefined }) {
  if (!pub) return <span className="text-xs text-slate-400">—</span>;
  if (pub.status === "published") {
    const inner = (
      <>
        <CheckCircle2 className="h-3.5 w-3.5" /> Terkirim
      </>
    );
    return pub.externalUrl ? (
      <a
        href={pub.externalUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-xs font-medium text-green-600 hover:underline"
      >
        {inner} <ExternalLink className="h-3 w-3" />
      </a>
    ) : (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600">
        {inner}
      </span>
    );
  }
  if (pub.status === "failed") {
    return (
      <span
        title={pub.errorMsg ?? ""}
        className="inline-flex items-center gap-1 text-xs font-medium text-rose-600"
      >
        <XCircle className="h-3.5 w-3.5" /> Gagal
      </span>
    );
  }
  return <span className="text-xs text-amber-600">Menunggu…</span>;
}
